// Premium animated hero — Sri Yantra sacred geometry + lotus mandala + breathing ॐ +
// drifting golden particles + occasional shooting stars. Pure SVG + CSS, no images.
import { useMemo } from "react";

export default function HeroCelestial() {
    // Generate 18 floating golden particles with random positions/durations once
    const particles = useMemo(
        () =>
            Array.from({ length: 18 }).map((_, i) => ({
                id: i,
                left: `${10 + Math.random() * 80}%`,
                size: 2 + Math.random() * 3,
                delay: `${-Math.random() * 8}s`,
                duration: `${8 + Math.random() * 6}s`,
                opacity: 0.4 + Math.random() * 0.5,
            })),
        [],
    );

    return (
        <div
            aria-hidden="true"
            data-testid="hero-celestial"
            className="relative aspect-square w-full max-w-[560px] mx-auto select-none overflow-hidden rounded-full"
        >
            {/* Nebula glow background */}
            <div className="absolute inset-0 rounded-full" style={{
                background: "radial-gradient(circle at 50% 50%, rgba(197,162,77,0.22) 0%, rgba(138,114,54,0.08) 35%, rgba(11,16,32,0) 70%)",
            }} />

            {/* Twinkling stars */}
            <div className="absolute inset-0 star-field opacity-70" />

            {/* Slow shooting-star streaks (2 of them, staggered) */}
            <div className="absolute inset-0">
                <span className="meteor" style={{ top: "18%", left: "-10%", animationDelay: "0s" }} />
                <span className="meteor" style={{ top: "62%", left: "-10%", animationDelay: "6s" }} />
            </div>

            {/* Outer lotus mandala — 12 petals, slow rotate */}
            <svg viewBox="0 0 600 600" className="absolute inset-0 w-full h-full animate-[spin_140s_linear_infinite]">
                <defs>
                    <radialGradient id="petal" cx="50%" cy="0%" r="100%">
                        <stop offset="0%" stopColor="#E5C97A" stopOpacity="0.5" />
                        <stop offset="60%" stopColor="#C5A24D" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="#8A7236" stopOpacity="0" />
                    </radialGradient>
                </defs>
                {Array.from({ length: 12 }).map((_, i) => (
                    <g key={i} transform={`rotate(${i * 30} 300 300)`}>
                        <path
                            d="M 300 60 C 332 130 332 200 300 270 C 268 200 268 130 300 60 Z"
                            fill="url(#petal)"
                            stroke="rgba(197,162,77,0.35)"
                            strokeWidth="0.6"
                        />
                    </g>
                ))}
                <circle cx="300" cy="300" r="270" fill="none" stroke="rgba(197,162,77,0.25)" strokeWidth="0.5" />
                <circle cx="300" cy="300" r="245" fill="none" stroke="rgba(197,162,77,0.15)" strokeWidth="0.4" strokeDasharray="1 4" />
            </svg>

            {/* Middle Sri Yantra triangles — counter rotate slowly with breathing pulse */}
            <div className="absolute inset-[14%] animate-[breath_8s_ease-in-out_infinite]">
                <svg viewBox="-100 -100 200 200" className="w-full h-full animate-[spin_220s_linear_infinite_reverse]">
                    <g fill="none" stroke="#C5A24D" strokeWidth="0.45" opacity="0.9">
                        {/* 4 downward (Shakti) triangles */}
                        <polygon points="0,-80  -69,40  69,40"  strokeOpacity="0.85" />
                        <polygon points="0,-60  -52,30  52,30"  strokeOpacity="0.6" />
                        <polygon points="0,-46  -40,22  40,22"  strokeOpacity="0.5" />
                        <polygon points="0,-32  -28,16  28,16"  strokeOpacity="0.4" />
                        {/* 5 upward (Shiva) triangles */}
                        <polygon points="0,80   -69,-40 69,-40" strokeOpacity="0.85" />
                        <polygon points="0,64   -55,-32 55,-32" strokeOpacity="0.65" />
                        <polygon points="0,50   -43,-25 43,-25" strokeOpacity="0.55" />
                        <polygon points="0,36   -31,-18 31,-18" strokeOpacity="0.45" />
                        <polygon points="0,22   -19,-11 19,-11" strokeOpacity="0.35" />
                    </g>
                    {/* outer circle */}
                    <circle cx="0" cy="0" r="88" fill="none" stroke="rgba(197,162,77,0.35)" strokeWidth="0.5" />
                    <circle cx="0" cy="0" r="92" fill="none" stroke="rgba(197,162,77,0.2)"  strokeWidth="0.4" strokeDasharray="2 3" />
                </svg>
            </div>

            {/* Inner glowing ॐ — breathing halo */}
            <div className="absolute inset-0 grid place-items-center">
                <div className="relative">
                    {/* halo rings */}
                    <div className="absolute -inset-10 rounded-full bg-gold/15 blur-2xl animate-[breath_6s_ease-in-out_infinite]" />
                    <div className="absolute -inset-6  rounded-full bg-gold/25 blur-xl  animate-[breath_4s_ease-in-out_infinite_0.4s]" />
                    {/* gold disk */}
                    <div className="relative h-24 w-24 rounded-full grid place-items-center" style={{
                        background: "radial-gradient(circle at 35% 30%, #F2D98C 0%, #C5A24D 55%, #8A7236 100%)",
                        boxShadow: "0 0 40px rgba(197,162,77,0.55), inset 0 0 24px rgba(11,16,32,0.25)",
                    }}>
                        <span className="font-serif text-5xl text-ink-deep" style={{ textShadow: "0 1px 0 rgba(245,240,235,0.45)" }}>ॐ</span>
                    </div>
                </div>
            </div>

            {/* Drifting golden particles */}
            {particles.map((p) => (
                <span
                    key={p.id}
                    className="particle"
                    style={{
                        left: p.left,
                        width: p.size,
                        height: p.size,
                        opacity: p.opacity,
                        animationDelay: p.delay,
                        animationDuration: p.duration,
                    }}
                />
            ))}

            {/* Local styles for the bespoke keyframes */}
            <style>{`
                @keyframes breath {
                    0%,100% { transform: scale(1);   opacity: 1; }
                    50%     { transform: scale(1.04); opacity: 0.82; }
                }
                @keyframes drift {
                    0%   { transform: translateY(110%) scale(0.4); opacity: 0; }
                    15%  { opacity: 0.9; }
                    100% { transform: translateY(-110%) scale(1);  opacity: 0; }
                }
                @keyframes meteor {
                    0%   { transform: translateX(0)    translateY(0)    rotate(15deg); opacity: 0; }
                    10%  { opacity: 1; }
                    100% { transform: translateX(140%) translateY(80%)  rotate(15deg); opacity: 0; }
                }
                .particle {
                    position: absolute;
                    bottom: -10%;
                    border-radius: 999px;
                    background: #E5C97A;
                    box-shadow: 0 0 8px rgba(229,201,122,0.9);
                    animation-name: drift;
                    animation-timing-function: ease-in-out;
                    animation-iteration-count: infinite;
                }
                .meteor {
                    position: absolute;
                    width: 120px; height: 1.5px;
                    background: linear-gradient(90deg, rgba(245,240,235,0), #E5C97A, rgba(245,240,235,0));
                    box-shadow: 0 0 8px rgba(229,201,122,0.85);
                    animation: meteor 7s linear infinite;
                }
            `}</style>
        </div>
    );
}
